import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on, Action } from '@ngrx/store';

import * as AuthActions from './auth.actions';
import { AuthEntity } from './auth.models';
import { IProfileModel } from '@piqet-angular/ui';
import { DropMessageTypeEnum } from '@piqet-angular/shared';
export const AUTH_FEATURE_KEY = 'auth';

export interface AuthState extends EntityState<AuthEntity> {
  selectedId?: string | number; // which Auth record has been selected
  loaded: boolean; // has the Auth list been loaded
  error?: string | null; // last known error (if any)
  profile: IProfileModel[];
  token: string | null;
  isRegistered: boolean;
  isLoggedOut: boolean;
  forgotPasswordData: any;
  changePasswordData: any;
  resetPasswordData?: any;
  isForgotPassword: boolean;
  isLoggedIn: boolean;
  activeUSerStatus: any;
  ipAdressData: any;
  sessionId: any;
  showOtpPage: boolean;
  userId: any;
  otpError: string;
  passwordUpdated: boolean;
  maxAttemptsReached: boolean;
}

export interface AuthPartialState {
  readonly [AUTH_FEATURE_KEY]: AuthState;
}

export const authAdapter: EntityAdapter<AuthEntity> = createEntityAdapter<AuthEntity>();

export const initialAuthState: AuthState = authAdapter.getInitialState({
  // set initial required properties
  loaded: false,
  profile: [],
  token: '',
  isRegistered: false,
  isLoggedOut: false,
  forgotPasswordData: {},
  resetPasswordData: {},
  isForgotPassword: false,
  isLoggedIn: false,
  activeUSerStatus: {},
  changePasswordData: {},
  ipAdressData: [],
  sessionId: '',
  showOtpPage: false,
  otpError: '',
  userId: '',
  passwordUpdated: false,
  maxAttemptsReached: false,
});

const reducer = createReducer(
  initialAuthState,
  //reducer for login user.
  on(AuthActions.login, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null, maxAttemptsReached: false };
  }),
  on(AuthActions.loginSuccess, (state, { profile, tokenClaim, isLoggedIn, isFlag, sessionId }) => {
    return {
      ...state,
      profile: profile || state?.profile,
      token: tokenClaim,
      isLoggedIn: isLoggedIn || state.isLoggedIn,
      isFlag: isFlag,
      sessionId: sessionId,
      loaded: true,
      maxAttemptsReached: false,
    };
  }),
  on(AuthActions.loginFailure, (state, { error, messageType }) => {
    const errorType =
      messageType === DropMessageTypeEnum.LoginValidation
        ? error
        : 'An unexpected error has occurred while processing your request. Please try again later or contact support if the issue persists.';
    return { ...state, error: errorType };
  }),
  on(AuthActions.maxLoginAttemptsReached, state => ({
    ...state,
    maxAttemptsReached: true,
  })),
  on(AuthActions.clearMaxLoginAttempts, state => ({
    ...state,
    maxAttemptsReached: false,
  })),
  on(AuthActions.verifyOtpFailure, (state, { error, messageType }) => {
    return { ...state, otpError: error };
  }),
  on(AuthActions.clearLoginData, state => ({
    ...state,
    profile: [],
    token: '',
    isLoggedIn: false,
  })),
  on(AuthActions.clearLoginMessageError, state => ({ ...state, error: '' })),

  //for Get Ip Address
  on(AuthActions.getIpAddressSuccess, (state, { ipAdressData }) => {
    return { ...state, ipAdressData: ipAdressData, loaded: true };
  }),
  //reducer for forgotPAssword.
  on(AuthActions.forgotPassword, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AuthActions.forgotPasswordSuccess, (state, { forgotPasswordData, isForgotPassword }) => {
    return {
      ...state,
      forgotPasswordData: forgotPasswordData,
      isForgotPassword: isForgotPassword,
      loaded: true,
    };
  }),
  on(AuthActions.forgotPasswordFailure, (state, { error }) => ({ ...state, error })),
  //reducer for changePassword.
  on(AuthActions.changePassword, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AuthActions.changePasswordSuccess, (state, { changePasswordData, passwordUpdated }) => {
    return {
      ...state,
      changePasswordData: changePasswordData,
      passwordUpdated: passwordUpdated,
      loaded: true,
    };
  }),
  on(AuthActions.changePasswordFailure, (state, { error }) => ({ ...state, error })),
  //reducer for forceful update password
  on(AuthActions.passwordUpdated, (state, {}) => ({ ...state, passwordUpdated: false })),
  //reducer for resetPassword.
  on(AuthActions.resetPassword, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AuthActions.resetPasswordSuccess, (state, { resetPasswordData }) => {
    return { ...state, resetPasswordData: resetPasswordData, loaded: true };
  }),
  on(AuthActions.resetPasswordFailure, (state, { error }) => ({ ...state, error })),
  //reducer for register user.
  on(AuthActions.register, (state, payload) => {
    return { ...state, payload: payload, error: null };
  }),
  on(AuthActions.registerSuccess, (state, { isRegistered }) => {
    return { ...state, isRegistered: isRegistered };
  }),
  on(AuthActions.registerFailure, (state, { error }) => ({ ...state, error })),
  //reducer for logout user.
  on(AuthActions.logout, state => {
    return { ...state, loaded: false, error: null };
  }),
  on(AuthActions.logoutSuccess, (state, { isLoggedOut }) => {
    return { ...state, isLoggedOut: isLoggedOut, loaded: true };
  }),
  on(AuthActions.logoutFailure, (state, { error }) => ({ ...state, error })),
  //reducer for logout user.
  on(AuthActions.activeUser, state => {
    return { ...state, loaded: false, error: null };
  }),
  on(AuthActions.activeUserSuccess, (state, { activeUSerStatus }) => {
    return { ...state, activeUSerStatus: activeUSerStatus, loaded: true };
  }),
  on(AuthActions.activeUserFailure, (state, { error }) => ({ ...state, error })),

  on(AuthActions.payment, state => {
    return { ...state, loaded: false, error: null };
  }),
  on(AuthActions.clearVerifyError, state => {
    return { ...state, error: null, otpError: '' };
  }),
  on(AuthActions.paymentSuccess, (state, { paymentStatus }) => {
    return { ...state, paymentStatus: paymentStatus, loaded: true };
  }),
  on(AuthActions.paymentFailure, (state, { error }) => ({ ...state, error })),

  on(AuthActions.clearErrorMessage, state => ({
    ...state,
    forgotPasswordData: [],
    changePasswordData: null,
  })),
  on(AuthActions.showOtpPage, (state, { showOtpPage, userId }) => ({
    ...state,
    showOtpPage: showOtpPage,
    userId: userId,
  }))
);

export function authReducer(state: AuthState | undefined, action: Action) {
  return reducer(state, action);
}
